<template>
    <div>
        <div class="form-row">
            <div id="loader" v-if="isLoading">
                <div class="spinner"></div>
            </div>

            <div class="form-group col-lg-6">
                <h3>Your phone number</h3>
                <h5 >We may use this phone number to contact you if we need to:</h5>
                <ul>
                    <li>share more information with you about the Information Session</li>
                    <li>help you with something e.g. joining the session</li>
                </ul>

                <label for="phone_number"
                class="form-label"
                    ><b>Phone number </b> <i class="fas fa-asterisk"></i
                ></label>
                <input
                    data-test="phone-number"
                    type="tel"
                    id="phone_number"
                    class="form-control"
                    name="phone_number"
                    autocomplete="tel"
                    v-model="formRef.phone_number"
                    required
                />
                <span
                    data-test="phone-number-errors"
                    class="enquiry-errors"
                    v-if="formRef.errors.has('phone_number')"
                    v-text="formRef.errors.get('phone_number')"
                    role="alert"
                    aria-live="assertive"
            ></span>
            </div>
        </div>

        <div class="form-row">
            <div class="form-group col-lg-6">
                <div data-test="post-code-lookup-component">
                    <br/>
                    <h3>Your location</h3>
                    <h5 class="py-3">Confirm you are in the right location for this Information Session.</h5>
                    <ol class="py-3">
                        <li>Enter your postcode</li>
                        <li>Select Search for address</li>
                        <li>Select your address from the list</li>
                    </ol>
                    <label for="postcodeLookup"
                    class="form-label"
                        ><b>Postcode </b><i class="fas fa-asterisk"></i
                    ></label>
                    <post-code-look-up
                        v-on:addressChanged="updateAddress"
                        v-model="formRef.post_code"
                    ></post-code-look-up>
                     <span
                        class="enquiry-errors"
                        data-test="postcode-errors"
                        v-if="
                            formRef.errors.has('post_code') && !showNoAddressError
                        "
                        v-text="formRef.errors.get('post_code')"
                        role="alert"
                        aria-live="assertive"
                    >
                    </span>

                    <span
                        class="enquiry-errors"
                        v-if="showNoAddressError"
                        data-test="post-code-lookup-error"
                        role="alert"
                        aria-live="assertive"
                    >
                        An address is required.
                    </span>
                </div>

                <fieldset
                    v-if="formRef.post_code"
                    data-test="full-address-fields"
                >
                    <div class="py-3">
                        <label for="house" class="form-label">House/Flat Number</label>
                        <input
                            data-test="address-line-1"
                            id="house"
                            name="addressLine1"
                            v-model="formRef.addressLine1"
                            class="form-control"
                        />
                        <span
                            class="enquiry-errors"
                            v-if="formRef.errors.has('addressLine1')"
                            v-text="formRef.errors.get('addressLine1')"
                            role="alert"
                            aria-live="assertive"
                        >
                        </span>
                    </div>
                    <div class="py-3">
                        <label for="building" class="form-label">Address Line 2</label>
                        <input
                            id="building"
                            name="addressLine2"
                            v-model="formRef.addressLine2"
                            class="form-control"
                        />
                        <span
                            class="enquiry-errors"
                            v-if="formRef.errors.has('addressLine2')"
                            v-text="formRef.errors.get('addressLine2')"
                            role="alert"
                            aria-live="assertive"
                        >
                        </span>
                    </div>
                    <div class="py-3">
                        <label for="street" class="form-label">Address Line 3</label>
                        <input
                            id="street"
                            name="addressLine3"
                            v-model="formRef.addressLine3"
                            class="form-control"
                        />
                        <span
                            class="enquiry-errors"
                            v-if="formRef.errors.has('addressLine3')"
                            v-text="formRef.errors.get('addressLine3')"
                            role="alert"
                            aria-live="assertive"
                        >
                        </span>
                    </div>
                    <div class="py-3">
                        <label for="suburb" class="form-label">Town</label>
                        <input
                            id="suburb"
                            name="locality"
                            v-model="formRef.locality"
                            class="form-control"
                        />
                        <span
                            class="enquiry-errors"
                            v-if="formRef.errors.has('locality')"
                            v-text="formRef.errors.get('locality')"
                            role="alert"
                            aria-live="assertive"
                        >
                        </span>
                    </div>
                    <input
                        type="hidden"
                        id="district"
                        name="district"
                        v-model="formRef.district"
                        class="form-control"
                    />
                    <input
                        type="hidden"
                        id="ward"
                        name="ward"
                        v-model="formRef.ward"
                        class="form-control"
                    />
                    <div class="py-3">
                        <label for="post_code" class="form-label">Post code</label>
                        <input
                            id="post_code"
                            name="post_code"
                            v-model="formRef.post_code"
                            class="form-control"
                        />
                        <span
                            class="enquiry-errors"
                            v-if="formRef.errors.has('post_code')"
                            v-text="formRef.errors.get('post_code')"
                            role="alert"
                            aria-live="assertive"
                        >
                        </span>
                    </div>
                </fieldset>
            </div>
        </div>
        <div class="form__buttons form-group py-3">
            <button
                type="button"
                class="btn btn-secondary me-2"
                @click.prevent="goBack()"
                data-test="btn-back"
            >
                Go back
            </button>
            <button
                class="btn btn-primary"
                @click.prevent="nextPage()"
                data-test="btn-next"
            >
                Next
            </button>
        </div>
    </div>
</template>

<script setup>
import PostCodeLookUp from "../../../PostCodeLookUp";
import Form from "../../../../Form.js";
import { ref, markRaw, computed } from "vue";

const props = defineProps({
    step: Number,
    formData: Object
})

const emit = defineEmits(['goBackAStep', 'submitButton'])

let formRef = markRaw(ref(new Form({
    phone_number: null,
    addressLine1: null,
    addressLine2: null,
    addressLine3: null,
    locality: null,
    post_code: null,
    district: null,
    ward: null
}, props.formData)))

const isLoading = ref(false)
const address = {
    populated: false,
    submitted: false
}

let showNoAddressError = computed(() => {
    return !address.populated && address.submitted
})

function updateAddress(payLoad) {
    scrollToAddress();
    address.populated = true;

    const formattedLines = payLoad?.result?.addresses_formatted
        ?.find((layout) => layout.layout_name === "PrincesT")
        ?.address_lines || payLoad?.result?.addresses_formatted?.[0]?.address_lines;

    if (formattedLines && formattedLines.length) {
        const unlabeledLines = formattedLines
            .filter((line) => !line.label && line.line)
            .map((line) => line.line.trim())
            .filter(Boolean);

        const byLabel = formattedLines.reduce((acc, line) => {
            const key = (line.label || "").trim().toLowerCase();
            if (key) {
                acc[key] = (line.line || "").trim();
            }

            return acc;
        }, {});

        formRef.value.addressLine1 = unlabeledLines[0] || "";
        formRef.value.addressLine2 = unlabeledLines[1] || "";
        formRef.value.addressLine3 = unlabeledLines[2] || byLabel["county"] || "";
        formRef.value.locality = byLabel["town"] || "";
        formRef.value.post_code = byLabel["postcode"] || "";
        formRef.value.district = byLabel["local authority name"] || "";
        formRef.value.ward = byLabel["local authority ward name"] || "";

        return;
    }

    // Backward compatibility with old address array payload.
    let addressArray = Array.isArray(payLoad) ? payLoad : [];
    if (!addressArray.length) {
        return;
    }
    formRef.value.addressLine1 = addressArray[0]["addressLine1"];
    formRef.value.addressLine2 = addressArray[1]["addressLine2"];
    formRef.value.addressLine3 = addressArray[2]["addressLine3"];
    formRef.value.locality = addressArray[3]["locality"];
    formRef.value.post_code = addressArray[5]["postalCode"];
    formRef.value.district = addressArray[7]["district"];
    formRef.value.ward = addressArray[8]["ward"];
}

function scrollToAddress() {
    const address = document.getElementsByClassName(
        "postcode-lookup-addresses"
    )[0];

    if (address) {
        const parent = address.parentNode;
        const { bottom } = parent.getBoundingClientRect();

        const offsetY = Math.max(0, window.pageYOffset + bottom - 30);
        window.scrollTo({ top: offsetY, behavior: "smooth" });
    }
}

function goBack() {
    emit("goBackAStep");
}

function nextPage() {
    isLoading.value = true;
    return formRef.value
        .post("/registration/step3")
        .then(() => {
            emit("submitButton", {
                data: {
                    phone_number: formRef.value.phone_number,
                    addressLine1: formRef.value.addressLine1,
                    addressLine2: formRef.value.addressLine2,
                    addressLine3: formRef.value.addressLine3,
                    locality: formRef.value.locality,
                    post_code: formRef.value.post_code,
                    district: formRef.value.district,
                    ward: formRef.value.ward
                }
            });
        })
        .catch(err => {
            formRef.value.errors.record(err);
        })
        .finally(() => (isLoading.value = false));
}
</script>
